<template>
    <section class="py-12">
      <div class="container mx-auto px-4 max-w-[2200px]">
        <!-- Section Header -->
        <h2 class="text-4xl font-bold mb-2 dark:text-white">Picked for you</h2>
        <p class="text-lg text-gray-600 dark:text-blue-200 mb-8">Experience the ultimate comfort and style.</p>
  
        <!-- Vehicle Carousel -->
        <div class="relative" @mouseenter="isHovered = true" @mouseleave="isHovered = false">
          <Carousel 
            class="w-full"
            @init-api="onInit"
            :opts="{
              align: 'start',
              dragFree: true,
              containScroll: 'trimSnaps',
              skipSnaps: true,
              dragThreshold: 3,
              duration: 30
            }"
            ref="carouselRef"
          >
            <CarouselContent 
              class="-ml-4" 
              @wheel="handleWheel"
            >
              <CarouselItem 
                v-for="(vehicle, index) in visibleVehicles" 
                :key="vehicle.identifier || index"
                class="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
              >
                <div class="bg-white dark:bg-black/70 dark:backdrop-blur-xs rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full border border-gray-100 dark:border-blue-900/40 flex flex-col dark:shadow-blue-900/20 dark:shadow-md">
                  <div class="relative pb-[66%] bg-gray-100 dark:bg-black/40 shrink-0">
                    <img 
                      v-if="vehicle.photos?.[0]"
                      :src="vehicle.photos[0]" 
                      :alt="vehicle.title"
                      loading="lazy"
                      class="absolute inset-0 w-full h-full object-cover"
                    />
                    <div v-else class="absolute inset-0 flex items-center justify-center">
                      <span class="text-gray-400 dark:text-blue-300/50">No image available</span>
                    </div>
                  </div>
                  <div class="p-4 flex flex-col grow dark:border-t dark:border-blue-900/20">
                    <!-- Content Section -->
                    <div class="grow">
                      <h3 class="text-xl font-medium line-clamp-1 mb-2 dark:text-blue-100">{{ vehicle.title }}</h3>
                      <div>
                        <p class="text-gray-600 dark:text-blue-200/80 text-sm line-clamp-3">
                          {{ vehicleDescriptions[index] }}
                        </p>
                        <p v-if="vehicle.Comments" class="text-gray-500 dark:text-blue-300/60 text-xs italic line-clamp-2 mt-1">
                          {{ vehicleComments[index] }}
                        </p>
                      </div>
                    </div>
                    
                    <!-- Footer Section -->
                    <div class="mt-4 space-y-4">
                      <!-- Price Section -->
                      <div class="space-y-1">
                        <p class="text-primary dark:text-blue-100 font-semibold text-lg flex items-center justify-between">
                          <span v-if="vehicle.price" class="dark:text-glow-blue">
                            ${{ formatPrice(vehicle.price) }}
                          </span>
                          <span v-else class="text-gray-700 dark:text-blue-200">
                            Contact Us
                          </span>
                          <span v-if="vehicle.price" class="text-sm text-gray-500 dark:text-blue-300/70 font-normal">
                            ${{ calculateWeeklyPayment(vehicle.price) }}/week
                          </span>
                        </p>
                        <div class="flex items-center justify-between text-xs text-gray-500 dark:text-blue-300/70">
                          <span>{{ vehicle.kms ? `${formatNumber(vehicle.kms)} km` : 'New Vehicle' }}</span>
                          <span>{{ vehicle.fuel?.displayValue?.[0] || '' }}</span>
                        </div>
                      </div>
                      
                      <!-- Status Section -->
                      <div class="pt-2 border-t border-gray-100 dark:border-blue-900/30">
                        <div class="flex items-center justify-between text-sm">
                          <div class="flex items-center gap-2">
                            <span class="px-2 py-1 bg-gray-100 dark:bg-blue-900/30 dark:backdrop-blur-xs rounded-full text-gray-700 dark:text-blue-200 uppercase">
                              {{ vehicle.condition?.displayValue?.[0] || 'New' }}
                            </span>
                            <span class="text-gray-500 dark:text-blue-300/70">
                              {{ vehicle.transmission?.displayValue?.[0] || 'Automatic' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            
            <!-- Custom Navigation -->
            <div class="absolute -left-4 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
              <CarouselPrevious @click="handleNavClick" class="relative -left-3 h-12 w-12 border-2 border-gray-200 dark:border-blue-800/30 bg-white/90 dark:bg-black/60 backdrop-blur-xs dark:text-blue-100" />
            </div>
            <div class="absolute -right-4 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
              <CarouselNext @click="handleNavClick" class="relative -right-3 h-12 w-12 border-2 border-gray-200 dark:border-blue-800/30 bg-white/90 dark:bg-black/60 backdrop-blur-xs dark:text-blue-100" />
            </div>
          </Carousel>
  
          <!-- Custom Scrollbar with Timer Indicator -->
          <div class="mt-8 px-4">
            <div class="flex items-center gap-2 mb-2">
              <div class="flex items-center gap-1.5">
                <div 
                  :class="[
                    'w-1.5 h-1.5 rounded-full transition-colors duration-300',
                    isAutoPlayActive ? 'bg-primary dark:bg-blue-400 dark:animate-pulse-glow' : 'bg-gray-300 dark:bg-blue-900/50'
                  ]"
                ></div>
                <span class="text-xs text-gray-500 dark:text-blue-300/80">Auto-play</span>
              </div>
              <div class="text-xs text-gray-400 dark:text-blue-400/50">
                <template v-if="autoPlayStatus === 'disabled'">(disabled)</template>
                <template v-else-if="autoPlayStatus === 'paused'">(paused)</template>
                <template v-else-if="autoPlayStatus === 'resuming'">(resuming...)</template>
              </div>
              <div v-if="isAutoPlayActive" class="ml-auto text-xs text-gray-500 dark:text-blue-300/80">
                Next slide in {{ autoPlayCountdown }}s
              </div>
            </div>
            <div class="w-full h-1 bg-gray-200 dark:bg-blue-900/30 rounded-full overflow-hidden cursor-pointer relative" @click="handleScrollbarClick">
              <div 
                class="h-full transition-all duration-300"
                :class="[isAutoPlayActive ? 'bg-primary dark:bg-blue-400' : 'bg-black dark:bg-blue-100']"
                :style="{ width: `${scrollPercentage}%` }"
              ></div>
              <div 
                v-if="isAutoPlayActive"
                class="absolute top-0 h-full bg-primary dark:bg-blue-400/50 opacity-30 dark:opacity-70"
                :style="{ 
                  width: `${2}%`, 
                  left: `${nextSlideIndicatorPosition}%`,
                  transition: 'left 0.3s ease-out'
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, onUnmounted, shallowRef, watch, nextTick } from 'vue'
  import { useVehiclesStore } from '~/stores/vehicles'
  import { calculateWeeklyPayment } from '~/layers/base/utils/finance'
  import Carousel from '~/layers/base/components/ui/carousel/Carousel.vue'
  import CarouselContent from '~/layers/base/components/ui/carousel/CarouselContent.vue'
  import CarouselItem from '~/layers/base/components/ui/carousel/CarouselItem.vue'
  import CarouselNext from '~/layers/base/components/ui/carousel/CarouselNext.vue'
  import CarouselPrevious from '~/layers/base/components/ui/carousel/CarouselPrevious.vue'
  import { throttle } from 'lodash-es'
  import { useNuxtApp } from '#app'
  
  const nuxtApp = useNuxtApp()
  const vehiclesStore = useVehiclesStore(nuxtApp.$pinia)
  
  // Use shallowRef for better performance with complex objects
  const api = shallowRef(null)
  const carouselRef = shallowRef(null)
  const scrollPercentage = ref(0)
  const isScrolling = ref(false)
  const isHovered = ref(false)
  const hasUserInteracted = ref(false)
  const lastInteractionTime = ref(Date.now())
  const autoPlayCountdown = ref(5)
  const nextSlideIndicatorPosition = ref(0)
  
  // Efficient vehicle rendering using virtualization concept
  const visibleVehicles = computed(() => {
    return vehiclesStore.vehicles.slice(0, 12)
  })
  
  // Pre-compute descriptions and comments to reduce template computations
  const vehicleDescriptions = computed(() => {
    return visibleVehicles.value.map(vehicle => getDescription(vehicle))
  })
  
  const vehicleComments = computed(() => {
    return visibleVehicles.value.map(vehicle => 
      vehicle.Comments ? formatComments(vehicle.Comments) : '')
  })
  
  // Computed properties for auto-play state with memoization
  const isAutoPlayActive = computed(() => {
    return !hasUserInteracted.value && 
           !isHovered.value && 
           (Date.now() - lastInteractionTime.value) >= 5000
  })
  
  const autoPlayStatus = computed(() => {
    if (hasUserInteracted.value) return 'disabled'
    if (isHovered.value) return 'paused'
    if ((Date.now() - lastInteractionTime.value) < 5000) return 'resuming'
    return 'active'
  })
  
  // Throttled handlers for better performance
  const handleWheel = throttle((event) => {
    if (!api.value || isScrolling.value) return
  
    // Only handle horizontal scrolling or when shift is pressed
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) {
      event.preventDefault()
      
      handleUserInteraction(false)
      
      const currentPosition = api.value.scrollProgress()
      const scrollSnapPositions = api.value.scrollSnapList()
      const totalSlides = scrollSnapPositions.length
      const currentIndex = Math.floor(currentPosition * totalSlides)
      
      const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0)
      if (delta > 0 && currentIndex < totalSlides - 1) {
        smoothScrollTo(currentIndex + 1)
      } else if (delta < 0 && currentIndex > 0) {
        smoothScrollTo(currentIndex - 1)
      }
    }
  }, 50)
  
  function handleNavClick() {
    handleUserInteraction(false)
  }
  
  // Optimized handling of user interaction
  function handleUserInteraction(permanent = false) {
    if (permanent) {
      hasUserInteracted.value = true
    }
    
    // Update the interaction timestamp
    lastInteractionTime.value = Date.now()
    
    // Stop current auto-scroll operations
    stopAutoScroll()
    
    // If not permanently disabled, we'll need to start a resuming process
    if (!hasUserInteracted.value) {
      // Reset autoplay countdown for visual feedback
      autoPlayCountdown.value = 5
      
      // In 5 seconds, check if we should resume
      setTimeout(() => {
        if (!hasUserInteracted.value && !isHovered.value) {
          // Force escape from "resuming" state
          lastInteractionTime.value = Date.now() - 5100
          // Start the auto-scroll
          startAutoScroll()
        }
      }, 5100)
    }
  }
  
  // Smooth scroll with animation frame for better performance
  function smoothScrollTo(index, duration = 300) {
    if (!api.value) return
    
    isScrolling.value = true
    api.value.scrollTo(index, { duration })
    
    // Reset scrolling state after animation completes
    setTimeout(() => {
      isScrolling.value = false
    }, duration + 50)
  }
  
  // Use requestAnimationFrame for smoother countdown animation
  let countdownRAF = null
  let lastCountdownTime = 0
  let countdownInterval = null
  let resumingTimer = null
  
  function updateCountdown(timestamp) {
    if (!lastCountdownTime) lastCountdownTime = timestamp
    
    const elapsed = timestamp - lastCountdownTime
    
    if (elapsed > 1000) {
      autoPlayCountdown.value = Math.max(0, autoPlayCountdown.value - 1)
      lastCountdownTime = timestamp
      
      // Update the next slide indicator position
      updateNextSlideIndicator()
    }
    
    if (isAutoPlayActive.value) {
      countdownRAF = requestAnimationFrame(updateCountdown)
    }
  }
  
  function updateNextSlideIndicator() {
    if (!api.value) return
    
    const progress = api.value.scrollProgress() || 0
    const scrollSnapPositions = api.value.scrollSnapList()
    const totalSlides = scrollSnapPositions.length
    const currentIndex = Math.floor(progress * totalSlides)
    
    // Calculate the position of the next slide
    const nextIndex = (currentIndex + 1) % totalSlides
    nextSlideIndicatorPosition.value = (nextIndex / totalSlides) * 100
  }
  
  // Optimized auto-scroll with better performance
  function startAutoScroll() {
    // Check auto-play eligibility
    const timeSinceInteraction = Date.now() - lastInteractionTime.value
    
    // If user has permanently disabled or we're in cooldown period
    if (hasUserInteracted.value) return
    
    // If we're still in the resuming period, schedule a future activation
    if (timeSinceInteraction < 5000) {
      const timeToWait = 5000 - timeSinceInteraction
      
      // Clear any existing timers first
      stopAutoScroll()
      
      // Schedule auto-scroll to start after the remaining cooldown
      setTimeout(() => {
        // Only start if conditions are still good
        if (!hasUserInteracted.value && !isHovered.value) {
          // Force the lastInteractionTime to be old enough
          lastInteractionTime.value = Date.now() - 5001
          startAutoScroll()
        }
      }, timeToWait + 100)
      
      return
    }
    
    // We're good to start - clean up first
    stopAutoScroll()
    
    // Reset countdown
    autoPlayCountdown.value = 5
    
    // Start visual countdown
    lastCountdownTime = 0
    countdownRAF = requestAnimationFrame(updateCountdown)
    
    // Update indicator position
    updateNextSlideIndicator()
    
    countdownInterval = setInterval(() => {
      if (!api.value || isHovered.value || isScrolling.value) return
      
      const progress = api.value.scrollProgress() || 0
      const scrollSnapPositions = api.value.scrollSnapList()
      const totalSlides = scrollSnapPositions.length
      const currentIndex = Math.floor(progress * totalSlides)
      
      // Only reset to start if we're at the very end
      if (progress >= 0.99) {
        smoothScrollTo(0)
      } else {
        // Otherwise, scroll to next item
        smoothScrollTo(currentIndex + 1)
      }
      
      // Reset countdown
      autoPlayCountdown.value = 5
      lastCountdownTime = 0
    }, 5000)
  }
  
  function stopAutoScroll() {
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    
    if (countdownRAF) {
      cancelAnimationFrame(countdownRAF)
      countdownRAF = null
    }
    
    if (resumingTimer) {
      clearTimeout(resumingTimer)
      resumingTimer = null
    }
  }
  
  // Optimized scrollbar click with proportional positioning
  function handleScrollbarClick(event) {
    if (!api.value || isScrolling.value) return
    
    handleUserInteraction(true)
    
    const scrollbar = event.currentTarget
    const { left, width } = scrollbar.getBoundingClientRect()
    const clickPosition = event.clientX - left
    const percentage = clickPosition / width
    
    // Use the scrollSnap API to find the closest snap point
    const scrollSnapPositions = api.value.scrollSnapList()
    const targetIndex = Math.round(percentage * (scrollSnapPositions.length - 1))
    
    // Smooth scroll to the clicked position
    smoothScrollTo(targetIndex)
  }
  
  // Initialize carousel API
  function onInit(carousel) {
    api.value = carousel
  
    // Update scroll indicator on scroll with throttling for better performance
    api.value.on('scroll', throttle(() => {
      if (!api.value || isScrolling.value) return
      const progress = api.value.scrollProgress() || 0
      scrollPercentage.value = progress * 100
      
      // Update the next slide indicator when scrolling manually
      if (isAutoPlayActive.value) {
        updateNextSlideIndicator()
      }
    }, 16)) // ~60fps
  
    // Track user interactions
    api.value.on('dragStart', () => {
      handleUserInteraction(true)
      isScrolling.value = true
    })
  
    api.value.on('dragEnd', () => {
      isScrolling.value = false
      
      // Add a small delay before snapping for a more natural feel
      nextTick(() => {
        const progress = api.value.scrollProgress() || 0
        const scrollSnapPositions = api.value.scrollSnapList()
        const targetIndex = Math.round(progress * (scrollSnapPositions.length - 1))
        
        // Smooth snap to nearest slide
        setTimeout(() => {
          api.value?.scrollTo(targetIndex, { duration: 300 })
        }, 50)
      })
    })
  
    api.value.on('select', () => handleUserInteraction(false))
  
    // Initial scroll position
    nextTick(() => {
      scrollPercentage.value = (api.value?.scrollProgress() || 0) * 100
    })
    
    // Start auto-scroll initially after a short delay
    setTimeout(() => {
      startAutoScroll()
    }, 1000)
  }
  
  // Watch for auto-play state changes
  // Monitor auto-play activation
  watch(isAutoPlayActive, (active) => {
    if (active) {
      startAutoScroll()
    } else {
      stopAutoScroll()
    }
  })
  
  // Create a dedicated timer to check resuming state
  let resumingCheckTimer = null
  
  // Monitor auto-play status specifically
  watch(autoPlayStatus, (status, prevStatus) => {
    // Clear any existing resuming check timer
    if (resumingCheckTimer) {
      clearTimeout(resumingCheckTimer)
      resumingCheckTimer = null
    }
    
    // If we're in the resuming state, set up a forced transition
    if (status === 'resuming') {
      // Force transition to active after the 5 second delay
      resumingCheckTimer = setTimeout(() => {
        // If we're still in resuming state after timeout, force activate
        if (autoPlayStatus.value === 'resuming' && !isHovered.value && !hasUserInteracted.value) {
          // Reset the last interaction time to force active state
          lastInteractionTime.value = Date.now() - 5001
          // Explicitly start auto-scroll
          startAutoScroll()
        }
      }, 5100) // Slightly longer than the resuming timeout
    }
  })
  
  // Optimized formatting functions with memoization
  const formatCache = new Map()
  
  function formatPrice(price) {
    if (!price) return '0'
    
    const cacheKey = `price-${price}`
    if (formatCache.has(cacheKey)) {
      return formatCache.get(cacheKey)
    }
    
    const numPrice = parseInt(price, 10)
    const formatted = numPrice.toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
    
    formatCache.set(cacheKey, formatted)
    return formatted
  }
  
  function formatNumber(num) {
    if (!num) return '0'
    
    const cacheKey = `number-${num}`
    if (formatCache.has(cacheKey)) {
      return formatCache.get(cacheKey)
    }
    
    const formatted = num.toLocaleString('en-AU')
    formatCache.set(cacheKey, formatted)
    return formatted
  }
  
  function formatComments(comments) {
    if (!comments) return ''
    
    const cacheKey = `comments-${comments.substring(0, 100)}`
    if (formatCache.has(cacheKey)) {
      return formatCache.get(cacheKey)
    }
    
    // Split into paragraphs and clean up
    const paragraphs = comments.split('\n\n')
    
    // Get the first non-empty paragraph that's not too long
    const relevantParagraph = paragraphs.find(p => 
      p.trim() && 
      !p.toLowerCase().includes('proudly part of') &&
      !p.toLowerCase().includes('at ferntree gully automotive')
    ) || paragraphs[0]
    
    // Clean up the text
    let cleanText = relevantParagraph
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
    
    // Limit to 2 lines worth of text
    const words = cleanText.split(/\s+/)
    const result = words.length <= 20 ? cleanText : words.slice(0, 20).join(' ') + '...'
    
    formatCache.set(cacheKey, result)
    return result
  }
  
  function getDescription(vehicle) {
    if (!vehicle) return ''
    
    const cacheKey = `description-${vehicle.identifier}`
    if (formatCache.has(cacheKey)) {
      return formatCache.get(cacheKey)
    }
    
    let result = ''
    
    // First try to use vehicle comments
    if (vehicle.Comments) {
      const paragraphs = vehicle.Comments.split('\n\n')
      // Get the first non-empty paragraph that's not too long
      const relevantParagraph = paragraphs.find(p => 
        p.trim() && 
        !p.toLowerCase().includes('proudly part of') &&
        !p.toLowerCase().includes('at ferntree gully automotive')
      ) || paragraphs[0] || '' // Ensure relevantParagraph is always a string
      
      const words = relevantParagraph.split(/\s+/)
      result = words.length <= 70 ? relevantParagraph : words.slice(0, 70).join(' ') + '...'
    } else {
      // Fallback to generated description
      result = `${vehicle.make?.displayValue?.[0] || ''} ${vehicle.model?.displayValue?.[0] || ''} ${vehicle.transmission?.displayValue?.[0] || ''} with ${vehicle.fuel?.displayValue?.[0] || ''} engine.`
    }
    
    // Normalize whitespace to prevent hydration errors
    const normalizedResult = result.replace(/\s+/g, ' ').trim();
    formatCache.set(cacheKey, normalizedResult)
    return normalizedResult
  }
  
  // Proper cleanup on component unmount
  onUnmounted(() => {
    stopAutoScroll()
    formatCache.clear()
    
    // Remove event listeners
    if (api.value) {
      api.value.off('scroll')
      api.value.off('dragStart')
      api.value.off('dragEnd')
      api.value.off('select')
    }
  })
  </script>
  
  <style scoped>

  /* Carousel customization */
  :deep(.embla__slide) {
    min-width: 0;
    touch-action: pan-y pinch-zoom;
    contain: content; /* Improves performance by isolating the rendering */
  }
  
  /* Smooth transitions */
  .transition-all {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 300ms;
  }
  
  /* Add momentum scrolling */
  :deep(.embla__container) {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain; /* Prevent page scroll during carousel swipe */
    will-change: transform; /* Hardware acceleration hint */
  }
  
  /* Optimized line clamp utilities with will-change for rendering performance */
  .line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    will-change: transform;
  }
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    will-change: transform;
  }
  
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    will-change: transform;
  }
  
/* Add to existing styles */
.bg-primary {
  background-color: black; /* Direct CSS for light mode */
}
.dark .bg-primary {
  background-color: hsl(217, 91%, 60%); /* Direct CSS for Tailwind's blue-400 */
}

.text-primary {
  color: black; /* Direct CSS for light mode */
}
.dark .text-primary {
  color: hsl(216, 100%, 95%); /* Direct CSS for Tailwind's blue-100 */
}

.dark .text-glow-blue {
    text-shadow: 0 0 8px rgba(96, 165, 250, 0.5);
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  .dark .animate-pulse-glow {
    animation: pulse-glow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse-glow {
    0%, 100% { 
      opacity: 1; 
      box-shadow: 0 0 5px rgba(96, 165, 250, 0.7);
    }
    50% { 
      opacity: 0.7; 
      box-shadow: 0 0 12px rgba(96, 165, 250, 0.9);
    }
  }
  
  /* Optimized image rendering */
  img {
    backface-visibility: hidden; /* Prevents flickering during animations */
    transform: translateZ(0); /* Forces GPU acceleration */
  }
  </style>
